import "../data/tactics";
import type { TacticAction } from "../types/actions";
import type { WarchestState } from "../types/game-state";
import { getTacticHandler } from "../utils/tactic-registry";
import {
  advanceActivePlayer,
  allHandsEmpty,
  moveCoin,
} from "../utils/zone-utils";

export function applyTactic(
  state: WarchestState,
  action: TacticAction,
): WarchestState {
  // Special case: ROYAL_GUARD_PROTECT uses the ROYAL coin to move a deployed Royal Guard
  if (action.coinType === "ROYAL" && action.unitHex) {
    const cell = state.board[action.unitHex];
    if (
      cell &&
      cell.owner === action.player &&
      cell.unitType === "ROYAL_GUARD"
    ) {
      const rgDef = state.config.units.ROYAL_GUARD;
      if (rgDef?.tacticId) {
        const handler = getTacticHandler(rgDef.tacticId);
        if (handler) return handler.apply(state, action);
      }
    }
  }

  const unitDef = state.config.units[action.coinType];
  if (!unitDef?.tacticId) return state;

  const handler = getTacticHandler(unitDef.tacticId);
  if (!handler) return state;

  // Discard coin face-up first, then apply tactic effect
  const zones = moveCoin(
    state.playerZones[action.player],
    action.coinType,
    "hand",
    "discardFaceUp",
  );
  const stateAfterDiscard = {
    ...state,
    playerZones: { ...state.playerZones, [action.player]: zones },
  };

  const stateAfterTactic = handler.apply(stateAfterDiscard, action);

  // If tactic ended the game, don't advance turn
  if (stateAfterTactic.phase === "GAME_END") return stateAfterTactic;

  const newPlayerZones = stateAfterTactic.playerZones;
  const nextActive = advanceActivePlayer(action.player, newPlayerZones);
  const nextPhase = allHandsEmpty(newPlayerZones)
    ? ("DRAW_COINS" as const)
    : stateAfterTactic.phase;
  return {
    ...stateAfterTactic,
    activePlayer: nextActive,
    phase: nextPhase,
    initiativeTransferredThisRound:
      nextPhase === "DRAW_COINS"
        ? false
        : stateAfterTactic.initiativeTransferredThisRound,
    turn:
      nextPhase === "DRAW_COINS"
        ? stateAfterTactic.turn + 1
        : stateAfterTactic.turn,
  };
}
